import { NgModule } from "@angular/core";
import {
  PreloadAllModules,
  Route,
  RouterModule,
  Routes,
} from "@angular/router";
import {
  wsRoutes as configRoutes,
  RouteConfigInstance,
} from "./routes.config";
import { OnlyAuthorizedGuard } from "@services/guards/only-authorized.guard";
import { HasPermissionGuard } from "@services/guards/has-permission.guard";
import {
  resolveReadPermissions,
  resolveWritePermissions,
} from "@services/permissions/permission-route.helper";

function finalizeFeatureRoute(
  route: Route & RouteConfigInstance
): Route & RouteConfigInstance {
  const permissionSectionBase = route.permissionSectionBase;
  const readPermission = resolveReadPermissions(route);
  const writePermission = resolveWritePermissions(route);
  const needsGuard =
    readPermission.length > 0 || !!permissionSectionBase;
  const mergedData = {
    ...(route.data ?? {}),
    ...(permissionSectionBase ? { permissionSectionBase } : {}),
    ...(readPermission.length ? { readPermission } : {}),
    ...(writePermission.length ? { writePermission } : {}),
  };
  const hasData = Object.keys(mergedData).length > 0;
  const out = {
    ...route,
    ...(hasData ? { data: mergedData } : {}),
  } as Route & RouteConfigInstance;
  if (needsGuard) {
    out.canActivate = [OnlyAuthorizedGuard, HasPermissionGuard];
  }
  const stripped = out as unknown as Record<string, unknown>;
  delete stripped["permission"];
  delete stripped["permissionSectionBase"];
  delete stripped["readPermission"];
  delete stripped["writePermission"];
  return out;
}

function buildRoutes(
  configs: RouteConfigInstance[],
  father?: RouteConfigInstance
): (Route & RouteConfigInstance)[] {
  return configs.reduce<(Route & RouteConfigInstance)[]>((acc, routeConfig) => {
    const route: Route & RouteConfigInstance = {
      ...(father ?? {}),
      ...routeConfig,
    };

    if (father && father.path) {
      route.path = `${father.path}/${route.path}`.replace(/\/{2,}/g, "/");
    }

    delete route.children;

    if (!routeConfig.loadChildren && !routeConfig.redirectTo) {
      route.children = buildRoutes(routeConfig.children!, routeConfig);
    }

    if (!routeConfig.redirectTo && !routeConfig.loadChildren) {
      const nested = (route.children ?? []) as (Route & RouteConfigInstance)[];
      acc.push(...nested);
    } else {
      acc.push(finalizeFeatureRoute(route));
    }
    return acc;
  }, []);
}

const routes: Routes = buildRoutes(configRoutes);

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      scrollPositionRestoration: "enabled",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
